import { useCallback, useState } from 'react';
import * as settingsService from '../services/settings.service';
import { PricingSettings } from '../services/settings.service';
import { withAuthRetry } from '../utils/withAuthRetry';

interface UseSettingsProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
  canEdit: boolean;
}

export function useSettings({ accessToken, refreshToken, attemptRefresh, canEdit }: UseSettingsProps) {
  const [minimumOrderValue, setMinimumOrderValue] = useState<number>(100);
  const [minimumOrderInput, setMinimumOrderInput] = useState<string>('100');
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);

  // Pricing settings state
  const [pricingSettings, setPricingSettings] = useState<PricingSettings>({
    cutCostPerUnit: 0.45,
    setupCostPerOrder: 2.0,
    marginRate: 0.35,
    marginTiers: null as string | null,
    taxRate: 0.1,
    vendorCommission: 0.05,
    managerCommission: 0.03
  });

  const [pricingInputs, setPricingInputs] = useState<Record<string, string>>({});
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingSaving, setPricingSaving] = useState(false);

  // Test calculator state
  const [testCalc, setTestCalc] = useState({
    materialCost: '0',
    quantity: '10',
    units: '1',
    // Dimension-based calculation
    width: '',
    height: '',
    thickness: '',
    density: '',
    pricePerKg: ''
  });

  const fetchSettings = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };

    setSettingsLoading(true);

    try {
      const value = await withAuthRetry(
        () => settingsService.fetchMinimumOrder(accessToken!),
        refreshToken,
        attemptRefresh
      );
      setMinimumOrderValue(value);
      setMinimumOrderInput(value.toString());
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro inesperado ao buscar configurações'
      };
    } finally {
      setSettingsLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh]);

  const saveMinimumOrder = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken || !canEdit) return { success: false, error: 'Não autorizado' };

    setSettingsSaving(true);

    try {
      const value = parseFloat(minimumOrderInput);
      if (isNaN(value) || value < 0) {
        return { success: false, error: 'Valor inválido' };
      }

      await withAuthRetry(
        () => settingsService.updateMinimumOrder(value, accessToken!),
        refreshToken,
        attemptRefresh
      );
      setMinimumOrderValue(value);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao salvar configuração'
      };
    } finally {
      setSettingsSaving(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, canEdit, minimumOrderInput]);

  const fetchPricingSettings = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };

    setPricingLoading(true);

    try {
      const data = await withAuthRetry(
        () => settingsService.fetchPricingSettings(accessToken!),
        refreshToken,
        attemptRefresh
      );
      setPricingSettings(data);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao carregar configurações de precificação'
      };
    } finally {
      setPricingLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh]);

  const savePricingSettings = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken || !canEdit) return { success: false, error: 'Não autorizado' };

    setPricingSaving(true);

    try {
      const updates: Partial<PricingSettings> = {};

      if (pricingInputs.cutCostPerUnit !== undefined) {
        updates.cutCostPerUnit = parseFloat(pricingInputs.cutCostPerUnit);
      }
      if (pricingInputs.setupCostPerOrder !== undefined) {
        updates.setupCostPerOrder = parseFloat(pricingInputs.setupCostPerOrder);
      }
      if (pricingInputs.marginRate !== undefined) {
        updates.marginRate = parseFloat(pricingInputs.marginRate);
      }
      if (pricingInputs.taxRate !== undefined) {
        updates.taxRate = parseFloat(pricingInputs.taxRate);
      }
      if (pricingInputs.vendorCommission !== undefined) {
        updates.vendorCommission = parseFloat(pricingInputs.vendorCommission);
      }
      if (pricingInputs.managerCommission !== undefined) {
        updates.managerCommission = parseFloat(pricingInputs.managerCommission);
      }
      if (pricingInputs.marginTiers !== undefined) {
        updates.marginTiers = pricingInputs.marginTiers || null;
      }

      await withAuthRetry(
        () => settingsService.updatePricingSettings(updates, accessToken!),
        refreshToken,
        attemptRefresh
      );
      await fetchPricingSettings();
      setPricingInputs({});
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao salvar configurações de precificação'
      };
    } finally {
      setPricingSaving(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, canEdit, pricingInputs, fetchPricingSettings]);

  return {
    // Minimum order state
    minimumOrderValue,
    minimumOrderInput,
    setMinimumOrderInput,
    settingsLoading,
    settingsSaving,

    // Pricing settings state
    pricingSettings,
    pricingInputs,
    setPricingInputs,
    pricingLoading,
    pricingSaving,

    // Test calculator state
    testCalc,
    setTestCalc,

    // Actions
    fetchSettings,
    saveMinimumOrder,
    fetchPricingSettings,
    savePricingSettings
  };
}
